import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Gift,
  Plus,
  RotateCcw,
  Lock,
} from "lucide-react";
import { GiftItemCard } from "@/components/customer/events/GiftItemCard";


import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";


import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { GiftImageField } from "@/components/customer/events/GiftImageField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import { readAutosaveDraft, useAutosave } from "@/hooks/useAutosave";
import { waitForPendingSaves } from "@/hooks/autosaveRegistry";
import { AutosaveStatus } from "@/components/common/AutosaveStatus";


import type { Event } from "@/services/api/events";
import {
  getGiftRegistry,
  updateGiftFund,
  deleteGiftFund,
  updateGiftRegistrySettings,
  refundCharge,
  type GiftFund,
  type GiftRegistryResponse,
  type RefundRequest,
} from "@/services/api/eventSettings";
import { fetchStripeAccountStatus } from "@/services/api/stripeConnect";
import { getUserCredits } from "@/services/api/eventPlans";
import {
  createGift,
  deleteGift,
  getEventGifts,
  updateGift,
  type GiftCategory,
  type GiftIndexResponse,
  type GiftItem,
  type GiftItemPayload,
} from "@/services/api/gifts";

/**
 * Which way out to offer an owner whose Gifts tab is locked.
 *
 * An owner who bought a plan from the pricing page has no event bound, so
 * fulfilment mints a redeemable credit (JUB-74) and the event stays free. Sending
 * them to buy again is how a paying customer gets stuck, so an owned credit wins.
 */
export function giftsLockedCallToAction(unusedCredits: number, locale: string) {
  return unusedCredits > 0
    ? { kind: "credit" as const, href: "?tab=general", testId: "apply-plan-credit-link",
        labelKey: "events.plan.applyCredit", bodyKey: "events.plan.giftsLockedHasCredit" }
    : { kind: "purchase" as const, href: `/${locale}/pachete`, testId: "view-plans-link",
        labelKey: "events.plan.viewPlans", bodyKey: "events.plan.giftsLockedDescription" };
}

interface GiftsTabProps {
  event: Event;
  locale: string;
}

export function GiftsTab({ event, locale }: GiftsTabProps) {
  const { t } = useTranslation();
  
  // Plan enforcement: check if event is on free plan (gifts locked)
  const isPlanFree = useMemo(() => {
    const plan = event.plan?.toLowerCase() || 'free';
    return plan === 'free';
  }, [event.plan]);

  // An unredeemed plan credit means the owner already paid — telling them to buy
  // again is how a paid customer ends up stuck on a locked Gifts tab.
  const [unusedCredits, setUnusedCredits] = useState(0);

  useEffect(() => {
    if (!isPlanFree) return;
    let cancelled = false;
    getUserCredits()
      .then((data) => {
        if (cancelled) return;
        setUnusedCredits((data?.standard ?? 0) + (data?.premium ?? 0));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [isPlanFree]);

  const lockedCta = giftsLockedCallToAction(unusedCredits, locale);

  const [registry, setRegistry] = useState<GiftRegistryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [stripeConnected, setStripeConnected] = useState<boolean>(false);
  const [giftData, setGiftData] = useState<GiftIndexResponse | null>(null);
  const [giftLoading, setGiftLoading] = useState(true);
  const [giftModal, setGiftModal] = useState<GiftModalState | null>(null);
  const [fundModal, setFundModal] = useState<FundModalState | null>(null);
  const [giftMutationLoading, setGiftMutationLoading] = useState(false);
  const [giftDeletingId, setGiftDeletingId] = useState<string | null>(null);
  const [giftTogglingId, setGiftTogglingId] = useState<string | null>(null);
  const [fundTogglingSlug, setFundTogglingSlug] = useState<string | null>(null);
  const [fundDeletingSlug, setFundDeletingSlug] = useState<string | null>(null);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState<{ id: string; amount: number; currency: string } | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refunding, setRefunding] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadRegistry() {
      setLoading(true);
      try {
        // JUB-152: a fund edit autosaved on close may still be in flight.
        await waitForPendingSaves(event.uuid);
        const [data, stripeStatus] = await Promise.all([
          getGiftRegistry(event.uuid),
          fetchStripeAccountStatus().catch(() => null),
        ]);
        if (!isMounted) return;
        setRegistry(data);
        setStripeConnected(Boolean(stripeStatus?.connected && stripeStatus?.charges_enabled));
      } catch (error) {
        console.error(error);
        toast.error(t("common.error"));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    loadRegistry();
    return () => {
      isMounted = false;
    };
  }, [event.uuid, t]);

  const loadGiftItems = useCallback(async () => {
    setGiftLoading(true);
    try {
      const data = await getEventGifts(event.uuid);
      setGiftData(data);
    } catch (error) {
      console.error(error);
      toast.error(t("events.form.giftRegistryLoadError"));
    } finally {
      setGiftLoading(false);
    }
  }, [event.uuid, t]);

  useEffect(() => {
    loadGiftItems();
  }, [loadGiftItems]);

  const updateFundState = (slug: string, patch: Partial<GiftFund>) => {
    setRegistry((current) => {
      if (!current) return current;
      return {
        ...current,
        funds: current.funds.map((fund) => (fund.slug === slug ? { ...fund, ...patch } : fund)),
      };
    });
  };

  const handleGiftToggle = async (gift: GiftItem, enabled: boolean) => {
    setGiftTogglingId(gift.id);
    try {
      await updateGift(event.uuid, gift.id, { enabled });
      toast.success(
        enabled
          ? t("events.form.giftEnabled")
          : t("events.form.giftDisabled")
      );
      loadGiftItems();
    } catch (error) {
      console.error(error);
      toast.error(t("events.form.giftToggleError"));
    } finally {
      setGiftTogglingId(null);
    }
  };

  const handleFundToggle = async (fund: GiftFund, enabled: boolean) => {
    setFundTogglingSlug(fund.slug);
    try {
      updateFundState(fund.slug, { enabled });
      await updateGiftFund(event.uuid, fund.slug, { enabled });
      toast.success(
        enabled
          ? t("gifts.fund.enabled")
          : t("gifts.fund.disabled")
      );
    } catch (error) {
      console.error(error);
      toast.error(t("gifts.fund.toggleError"));
      // Revert state on error
      updateFundState(fund.slug, { enabled: !enabled });
    } finally {
      setFundTogglingSlug(null);
    }
  };

  const handleGiftDelete = async (item: GiftItem) => {
    const confirmMessage = t("events.form.giftDeleteConfirm");
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setGiftDeletingId(item.id);
    try {
      await deleteGift(event.uuid, item.id);
      toast.success(t("events.form.giftDeleted"));
      loadGiftItems();
    } catch (error) {
      console.error(error);
      toast.error(t("events.form.giftDeleteError"));
    } finally {
      setGiftDeletingId(null);
    }
  };

  const handleFundDelete = async (fund: GiftFund) => {
    const confirmMessage = t("events.form.fundDeleteConfirm");
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setFundDeletingSlug(fund.slug);
    try {
      const updated = await deleteGiftFund(event.uuid, fund.slug);
      updateFundState(fund.slug, updated);
      toast.success(t("events.form.fundDeleted"));
    } catch (error: unknown) {
      console.error(error);
      const err = error as { response?: { status?: number }; message?: string };
      if (err?.response?.status === 409) {
        toast.error(t("events.form.fundDeleteHasContributions"));
      } else {
        toast.error(err?.message || t("events.form.fundDeleteError"));
      }
    } finally {
      setFundDeletingSlug(null);
    }
  };

  const handleGiftSubmit = async (payload: GiftItemPayload, mode: GiftModalState["mode"], gift?: GiftItem) => {
    setGiftMutationLoading(true);
    try {
      if (mode === "edit" && gift) {
        await updateGift(event.uuid, gift.id, payload);
        toast.success(t("events.form.giftUpdated"));
      } else {
        await createGift(event.uuid, payload);
        toast.success(t("events.form.giftCreated"));
      }
      setGiftModal(null);
      loadGiftItems();
    } catch (error) {
      console.error(error);
      toast.error(t("events.form.giftSaveError"));
    } finally {
      setGiftMutationLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalFundRaised =
      registry?.funds.reduce((sum, fund) => sum + fund.totalContributions, 0) ?? 0;
    const totalFundLive = registry?.funds.filter((fund) => fund.enabled).length ?? 0;
    const giftTotals = giftData?.gifts ?? [];
    const totalGiftRaised = giftTotals.reduce((sum, gift) => sum + gift.totalContributions, 0);
    const totalGiftTargets = giftTotals.reduce((sum, gift) => sum + gift.amount, 0);
    const totalGiftLive = giftTotals.filter((gift) => gift.enabled).length;

    // Calculate total from contributions, excluding refunded ones and deducting partial refunds
    const totalFromContributions = registry?.contributions
      .filter((c) => !c.refunded)
      .reduce((sum, c) => sum + Math.max(0, c.amount - (c.refundedAmount ?? c.refund?.amount ?? 0)), 0) ?? 0;

    return {
      totalRaised: totalFundRaised + totalGiftRaised,
      totalFromContributions, // Non-refunded contributions total
      fundsLive: totalFundLive,
      giftsLive: totalGiftLive,
      totalItemsLive: totalFundLive + totalGiftLive,
      pendingGoal: Math.max(totalGiftTargets - totalGiftRaised, 0),
    };
  }, [giftData, registry]);

  const registryColumnsReady = Boolean(!loading && registry && !giftLoading && giftData);

  if (!registryColumnsReady || !registry) {
    return (
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b">
          <CardTitle className="text-2xl">{t("events.form.gifts")}</CardTitle>
        </CardHeader>
        <CardContent className="py-12 text-center text-muted-foreground">{t("common.loading")}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Locked Banner - shown for free plan events */}
      {isPlanFree && (
        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertTitle className="text-amber-800 dark:text-amber-200">
            {t("events.plan.giftsLocked")}
          </AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-300 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>{t(lockedCta.bodyKey, { count: unusedCredits })}</span>
            <a
              href={lockedCta.href}
              data-testid={lockedCta.testId}
              className="inline-flex shrink-0 items-center justify-center rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
            >
              {t(lockedCta.labelKey)}
            </a>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards - displayed directly without wrapper card */}
      <div className="grid gap-4 md:grid-cols-2">
        <MetricCard
          label={t("events.form.totalGifts")}
          value={stats.totalItemsLive.toString()}
        />
        <MetricCard
          label={t("events.form.totalContributions")}
          value={formatCurrency(stats.totalRaised, event.currency ?? "RON")}
        />
      </div>

      {/* JUB-221: Owner gift contribution email notifications toggle */}
      <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 shadow-sm">
        <div className="space-y-0.5">
          <Label htmlFor="gift-email-notifications" className="text-sm font-medium">
            {t("events.form.giftEmailNotifications")}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t("events.form.giftEmailNotificationsDescription")}
          </p>
        </div>
        <Switch
          id="gift-email-notifications"
          data-testid="gift-email-notifications-switch"
          checked={registry.giftEmailNotifications ?? true}
          onCheckedChange={async (checked) => {
            const previous = registry.giftEmailNotifications ?? true;
            setRegistry((prev) => (prev ? { ...prev, giftEmailNotifications: checked } : prev));
            try {
              await updateGiftRegistrySettings(event.uuid, { giftEmailNotifications: checked });
              toast.success(t("common.saved"));
            } catch {
              setRegistry((prev) => (prev ? { ...prev, giftEmailNotifications: previous } : prev));
              toast.error(t("common.error"));
            }
          }}
        />
      </div>

      {/* Single unified section for all gifts */}
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Gift className="h-6 w-6 text-pink-600 dark:text-pink-400" />
              <CardTitle className="text-2xl">
                {t("events.form.gifts")}
              </CardTitle>
              {isPlanFree && (
                <Lock className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            {!isPlanFree && (
              <Button onClick={() => setGiftModal({ mode: "create" })}>
                <Plus className="mr-2 h-4 w-4" />
                {t("events.form.addGift")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Combined display of Funds and Gift Registry Items */}
          {registry.funds.length === 0 && (!giftData || giftData.gifts.length === 0) ? (
            <div className="flex flex-col items-center space-y-3 rounded-xl border border-dashed p-6 text-center text-muted-foreground">
              <p>{isPlanFree ? t("events.plan.giftsLocked") : t("events.form.noGiftsYet")}</p>
              {!isPlanFree && (
                <Button variant="outline" onClick={() => setGiftModal({ mode: "create" })}>
                  {t("events.form.addFirstGift")}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Cash Funds */}
              {registry.funds.map((fund) => (
                <GiftItemCard
                  key={fund.slug}
                  item={{
                    ...fund,
                    requiresBankLink: fund.requiresBankLink && !stripeConnected,
                  }}
                  type="fund"
                  locale={locale}
                  onToggle={isPlanFree ? undefined : (enabled) => handleFundToggle(fund, enabled)}
                  onEdit={isPlanFree ? undefined : () => setFundModal({ mode: "edit", fund })}
                  onDelete={isPlanFree ? undefined : () => handleFundDelete(fund)}
                  toggling={fundTogglingSlug === fund.slug}
                  deleting={fundDeletingSlug === fund.slug}
                  isLocked={isPlanFree}
                />
              ))}

              {/* Gift Registry Items */}
              {giftData?.gifts.map((gift) => (
                <GiftItemCard
                  key={gift.id}
                  item={gift}
                  type="gift"
                  locale={locale}
                  onToggle={isPlanFree ? undefined : (enabled) => handleGiftToggle(gift, enabled)}
                  onEdit={isPlanFree ? undefined : () => setGiftModal({ mode: "edit", gift })}
                  onDelete={isPlanFree ? undefined : () => handleGiftDelete(gift)}
                  toggling={giftTogglingId === gift.id}
                  deleting={giftDeletingId === gift.id}
                  isLocked={isPlanFree}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent contributions */}
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b">
          <CardTitle className="text-2xl">
            {t("events.form.recentContributions")}
          </CardTitle>
          <CardDescription>
            {t("events.form.recentContributionsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {registry.contributions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("events.form.noContributionsYet")}
            </p>
          ) : (
            <>
              {/* Desktop: table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-2 pr-4">{t("gifts.contributorName")}</th>
                      <th className="py-2 pr-4">{t("gifts.gift")}</th>
                      <th className="py-2 pr-4">{t("gifts.amount")}</th>
                      <th className="py-2 pr-4">{t("common.status")}</th>
                      <th className="py-2 pr-4">{t("common.date")}</th>
                      {stripeConnected && <th className="py-2">{t("common.actions")}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {registry.contributions.map((c) => (
                      <tr
                        key={c.id}
                        className={`border-b last:border-b-0 ${c.refunded ? "opacity-60 bg-muted/30" : ""}`}
                      >
                        <td className={`py-2 pr-4 font-medium ${c.refunded ? "line-through" : ""}`}>
                          {c.guest || "-"}
                        </td>
                        <td className={`py-2 pr-4 ${c.refunded ? "line-through" : ""}`}>
                          {c.giftName || "-"}
                        </td>
                        <td className={`py-2 pr-4 ${c.refunded ? "line-through" : ""}`}>
                          {formatCurrency(c.amount, c.currency || event.currency || "RON")}
                        </td>
                        <td className="py-2 pr-4">
                          <Badge
                            variant={
                              c.status === "paid"
                                ? "default"
                                : c.status === "refunded"
                                  ? "destructive"
                                  : "secondary"
                            }
                          >
                            {formatContributionStatus(t, c.status)}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex flex-col">
                            <span>
                              {c.createdAt
                                ? new Date(c.createdAt).toLocaleString(locale === "en" ? "en-GB" : "ro-RO")
                                : "-"}
                            </span>
                            {c.refund?.processedAt && (
                              <span className="text-xs text-muted-foreground">
                                {t("events.form.refundedOn")}:{" "}
                                {new Date(c.refund.processedAt).toLocaleString(locale === "en" ? "en-GB" : "ro-RO")}
                              </span>
                            )}
                          </div>
                        </td>
                        {stripeConnected && (
                          <td className="py-2">
                            {(c.status === "paid" || c.status === "partially_refunded") && !c.refunded && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const refundable = c.remainingRefundableAmount ?? c.amount;
                                  setSelectedContribution({ id: c.id, amount: refundable, currency: c.currency || event.currency || "RON" });
                                  setRefundAmount(refundable.toString());
                                  setRefundReason("");
                                  setRefundModalOpen(true);
                                }}
                                disabled={refunding}
                                data-testid="refund-contribution-button"
                              >
                                <RotateCcw className="mr-1 h-4 w-4" />
                                {t("events.form.refund")}
                              </Button>
                            )}
                            {c.refund && (
                              <div className="text-xs text-muted-foreground">
                                <div className="font-medium">
                                  {t("events.form.refundAmount")}: {formatCurrency(c.refund.amount, c.currency)}
                                </div>
                                {c.refund.reason && (
                                  <div className="mt-1 max-w-xs truncate" title={c.refund.reason}>
                                    {c.refund.reason}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile: cards */}
              <div className="md:hidden space-y-3">
                {registry.contributions.map((c) => (
                  <div
                    key={c.id}
                    className={`rounded-lg border p-4 ${c.refunded ? "opacity-60 bg-muted/30" : ""}`}
                  >
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex justify-between items-start">
                        <span className="font-medium text-muted-foreground">{t("gifts.contributorName")}</span>
                        <span className={`font-medium ${c.refunded ? "line-through" : ""}`}>{c.guest || "-"}</span>
                      </div>
                      <div className="flex justify-between items-start">
                        <span className="text-muted-foreground">{t("gifts.gift")}</span>
                        <span className={c.refunded ? "line-through" : ""}>{c.giftName || "-"}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">{t("gifts.amount")}</span>
                        <span className={c.refunded ? "line-through" : ""}>
                          {formatCurrency(c.amount, c.currency || event.currency || "RON")}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">{t("common.status")}</span>
                        <Badge
                          variant={
                            c.status === "paid" ? "default" : c.status === "refunded" ? "destructive" : "secondary"
                          }
                        >
                          {formatContributionStatus(t, c.status)}
                        </Badge>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{t("common.date")}</span>
                        <span>
                          {c.createdAt
                            ? new Date(c.createdAt).toLocaleString(locale === "en" ? "en-GB" : "ro-RO")
                            : "-"}
                        </span>
                        {c.refund?.processedAt && (
                          <span className="text-xs text-muted-foreground">
                            {t("events.form.refundedOn")}:{" "}
                            {new Date(c.refund.processedAt).toLocaleString(locale === "en" ? "en-GB" : "ro-RO")}
                          </span>
                        )}
                      </div>
                      {c.refund && (
                        <div className="text-xs text-muted-foreground">
                          <div className="font-medium">
                            {t("events.form.refundAmount")}: {formatCurrency(c.refund.amount, c.currency)}
                          </div>
                          {c.refund.reason && (
                            <div className="mt-1 truncate" title={c.refund.reason}>
                              {c.refund.reason}
                            </div>
                          )}
                        </div>
                      )}
                      {stripeConnected && (c.status === "paid" || c.status === "partially_refunded") && !c.refunded && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => {
                            const refundable = c.remainingRefundableAmount ?? c.amount;
                            setSelectedContribution({ id: c.id, amount: refundable, currency: c.currency || event.currency || "RON" });
                            setRefundAmount(refundable.toString());
                            setRefundReason("");
                            setRefundModalOpen(true);
                          }}
                          disabled={refunding}
                        >
                          <RotateCcw className="mr-1 h-4 w-4" />
                          {t("events.form.refund")}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <GiftModal
        open={Boolean(giftModal)}
        onOpenChange={(open) => {
          if (!open) {
            setGiftModal(null);
          }
        }}
        mode={giftModal?.mode ?? "create"}
        gift={giftModal?.gift}
        categories={giftData?.categories ?? []}
        loading={giftMutationLoading}
        onSubmit={handleGiftSubmit}
        eventUuid={event.uuid}
        onImageChange={() => loadGiftItems()}
      />

      <FundModal
        open={Boolean(fundModal)}
        onOpenChange={(open) => {
          if (!open) {
            setFundModal(null);
          }
        }}
        fund={fundModal?.fund}
        event={event}
        onImageChange={(cover) => {
          if (fundModal?.fund) {
            updateFundState(fundModal.fund.slug, { cover });
          }
        }}
        onSaved={(slug, updated) => updateFundState(slug, updated)}
      />

      {/* Refund Modal */}
      <Dialog open={refundModalOpen} onOpenChange={setRefundModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("events.form.refundContribution")}
            </DialogTitle>
            <DialogDescription>
              {t("events.form.refundContributionDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>
                {t("events.form.refundAmount")}
              </Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedContribution?.amount}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder={selectedContribution?.amount.toString()}
                  data-testid="refund-amount-input"
                />
                <span className="text-sm text-muted-foreground">
                  {selectedContribution?.currency}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t("events.form.refundAmountHint", {
                  amount: formatCurrency(selectedContribution?.amount || 0, selectedContribution?.currency || "RON"),
                })}
              </p>
            </div>
            <div>
              <Label>
                {t("events.form.refundReason")}
              </Label>
              <Textarea
                className="mt-2"
                rows={4}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t("events.form.refundReasonPlaceholder")}
                required
                data-testid="refund-reason-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("events.form.refundReasonHint")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRefundModalOpen(false);
                setSelectedContribution(null);
                setRefundReason("");
                setRefundAmount("");
              }}
              disabled={refunding}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={async () => {
                if (!selectedContribution || !refundReason.trim() || refundReason.trim().length < 10) {
                  toast.error(t("events.form.refundReasonRequired"));
                  return;
                }

                setRefunding(true);
                try {
                  const payload: RefundRequest = {
                    reason: refundReason.trim(),
                  };
                  if (refundAmount && parseFloat(refundAmount) < selectedContribution.amount) {
                    payload.amount = parseFloat(refundAmount);
                  }

                  await refundCharge(event.uuid, selectedContribution.id, payload);
                  toast.success(t("events.form.refundSuccess"));

                  // Reload registry and gift items in place to show updated status and totals
                  const [data, gifts] = await Promise.all([
                    getGiftRegistry(event.uuid),
                    getEventGifts(event.uuid),
                  ]);
                  setRegistry(data);
                  setGiftData(gifts);

                  setRefundModalOpen(false);
                  setSelectedContribution(null);
                  setRefundReason("");
                  setRefundAmount("");
                } catch (error: unknown) {
                  console.error("Refund error:", error);
                  toast.error((error as Error)?.message || t("events.form.refundError", {
                  }));
                } finally {
                  setRefunding(false);
                }
              }}
              disabled={refunding || !refundReason.trim() || refundReason.trim().length < 10}
              data-testid="process-refund-button"
            >
              {refunding
                ? t("events.form.processing")
                : t("events.form.processRefund")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatCurrency(value: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString()} ${currency}`;
  }
}

function formatContributionStatus(
  t: (key: string, params?: Record<string, string | number>) => string,
  status: string | null | undefined
): string {
  if (!status) return "-";
  const normalized = status.toLowerCase().trim();
  switch (normalized) {
    case "paid":
      return t("payments.status.paid");
    case "pending":
      return t("payments.status.pending");
    case "failed":
      return t("payments.status.failed");
    case "refunded":
      return t("payments.status.refunded");
    case "partially_refunded":
      return t("payments.status.partiallyRefunded");
    case "cancelled":
      return t("payments.status.cancelled");
    default:
      return status;
  }
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold text-foreground">{value}</p>
    </div>
  );
}

type GiftModalState = {
  mode: "create" | "edit";
  gift?: GiftItem;
};


type FundModalState = {
  mode: "edit";
  fund: GiftFund;
};

type GiftModalProps = {
  open: boolean;
  mode: "create" | "edit";
  gift?: GiftItem;
  categories: GiftCategory[];
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: GiftItemPayload, mode: GiftModalState["mode"], gift?: GiftItem) => void;
  eventUuid: string;
  /** The image was stored/removed server-side (JUB-39). */
  onImageChange: () => void;
};

function GiftModal({ open, onOpenChange, mode, gift, categories, loading, onSubmit, eventUuid, onImageChange }: GiftModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(gift?.name ?? "");
  const [amount, setAmount] = useState<string>(gift?.amount?.toString() ?? "");
  const [description, setDescription] = useState(gift?.description ?? "");
  const [link, setLink] = useState(gift?.link ?? "");
  const [location, setLocation] = useState(gift?.location ?? "");
  const [cover, setCover] = useState(gift?.cover ?? "");
  const [enabled, setEnabled] = useState(gift?.enabled ?? true);
  const [order, setOrder] = useState<string>(gift?.order?.toString() ?? "");
  const [categoryOption, setCategoryOption] = useState<string>("");
  const [categoryName, setCategoryName] = useState<string>(gift?.category?.name ?? "");

  useEffect(() => {
    if (gift) {
      setName(gift.name ?? "");
      setAmount(gift.amount ? String(gift.amount) : "");
      setDescription(gift.description ?? "");
      setLink(gift.link ?? "");
      setLocation(gift.location ?? "");
      setCover(gift.cover ?? "");
      setEnabled(gift.enabled ?? true);
      setOrder(gift.order ? String(gift.order) : "");
      setCategoryOption(gift.category?.id ? String(gift.category.id) : gift.category?.name ? "custom" : "");
      setCategoryName(gift.category?.name ?? "");
    } else if (mode === "create") {
      setName("");
      setAmount("");
      setDescription("");
      setLink("");
      setLocation("");
      setCover("");
      setEnabled(true);
      setOrder("");
      setCategoryOption("");
      setCategoryName("");
    }
  }, [gift, mode, categories]);

  useEffect(() => {
    // Avoid flashing raw ids (e.g. "17") while categories are still loading/normalizing.
    if (!open) return;
    if (categoryOption) return;
    if (gift?.category?.name) {
      setCategoryOption("custom");
      return;
    }
    const firstCategoryId = categories?.[0]?.id;
    if (firstCategoryId !== null && firstCategoryId !== undefined) {
      setCategoryOption(String(firstCategoryId));
    } else {
      setCategoryOption("custom");
    }
  }, [open, categoryOption, categories, gift]);

  const handleSubmit = () => {
    const payload: GiftItemPayload = {
      name,
      description,
      amount: amount ? Number(amount) : undefined,
      link: link || undefined,
      location: location || undefined,
      // The image is managed by the gift image endpoint (JUB-39), not this form.
      enabled,
      order: order ? Number(order) : undefined,
    };

    if (categoryOption === "custom") {
      payload.categoryName = categoryName || undefined;
    } else if (categoryOption) {
      payload.categoryId = categoryOption;
    }

    onSubmit(payload, mode, gift);
  };

  const categoryOptions = categories.map((category) => ({
    label: category.name,
    value: String(category.id),
  }));

  const selectedCategoryLabel =
    categoryOption === "custom"
      ? t("events.form.categoryCustom")
      : categoryOptions.find((opt) => opt.value === categoryOption)?.label;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? t("events.form.modalCreateGift")
              : t("events.form.modalEditGift")}
          </DialogTitle>
          <DialogDescription>
            {t("events.form.modalGiftDescription", {
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="gift-name">{t("events.form.giftName")}</Label>
            <Input
              id="gift-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("events.form.giftNamePlaceholder")}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("events.form.giftCategory")}</Label>
              <Select value={categoryOption} onValueChange={(value) => setCategoryOption(value)}>
                <SelectTrigger>
                  {selectedCategoryLabel ? (
                    <span>{selectedCategoryLabel}</span>
                  ) : (
                    <SelectValue placeholder={t("events.form.selectCategory")} />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">
                    {t("events.form.categoryCustom")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {categoryOption === "custom" && (
              <div className="space-y-2">
                <Label>{t("events.form.categoryName")}</Label>
                <Input
                  value={categoryName}
                  onChange={(event) => setCategoryName(event.target.value)}
                  placeholder={t("events.form.categoryNamePlaceholder")}
                />
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("events.form.goalAmount")}</Label>
              <Input
                type="number"
                min="0"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("events.form.sortOrder")}</Label>
              <Input
                type="number"
                min="0"
                value={order}
                onChange={(event) => setOrder(event.target.value)}
                placeholder="0"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("events.form.descriptionLabel")}</Label>
            <Textarea
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("events.form.giftDescriptionPlaceholder", {
              })}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("events.form.giftLink")}</Label>
              <Input
                value={link}
                onChange={(event) => setLink(event.target.value)}
                placeholder="https://jubiel.com/gift"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("events.form.giftLocation")}</Label>
              <Input
                value={location}
                onChange={(event) => setLocation(event.target.value)}
                placeholder={t("events.form.giftLocationPlaceholder")}
              />
            </div>
          </div>

          <div>
            <GiftImageField
              eventUuid={eventUuid}
              giftUuid={mode === "edit" ? gift?.id : null}
              label={t("events.form.giftCover")}
              value={cover}
              onChange={(url) => {
                setCover(url);
                onImageChange();
              }}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
            <div>
              <p className="font-medium text-foreground">
                {t("events.form.displayGift")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("events.form.displayGiftHint")}
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !name.trim()}>
            {loading
              ? t("common.loading")
              : mode === "create"
                ? t("events.form.saveGift")
                : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type FundModalProps = {
  open: boolean;
  fund?: GiftFund;
  event: Event;
  onOpenChange: (open: boolean) => void;
  /** A text edit was autosaved (JUB-152); the response is the stored fund. */
  onSaved: (slug: string, updated: Partial<GiftFund>) => void;
  /** The image was stored/removed server-side (JUB-39). */
  onImageChange: (cover: string) => void;
};

function FundModal({ open, onOpenChange, fund, event, onSaved, onImageChange }: FundModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Mounted only while open: closing unmounts the editor, which flushes
            (and, if the save fails, keeps) its unsaved edits. */}
        {fund && (
          <FundEditor
            key={fund.slug}
            fund={fund}
            event={event}
            onClose={() => onOpenChange(false)}
            onSaved={onSaved}
            onImageChange={onImageChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Fund text fields the edit dialog autosaves — never the image (JUB-39/152). */
type FundTextPayload = { name: string; description: string; thankYou: string; goal: number; enabled: boolean };
const FUND_AUTOSAVE_KEYS = ["name", "description", "thankYou", "goal", "enabled"] as const;

const fundText = (fund: GiftFund): FundTextPayload => ({
  name: fund.name ?? "",
  description: fund.description ?? "",
  thankYou: fund.thankYou ?? "",
  goal: fund.goal ?? 0,
  enabled: fund.enabled ?? true,
});

function FundEditor({
  fund,
  event,
  onClose,
  onSaved,
  onImageChange,
}: {
  fund: GiftFund;
  event: Event;
  onClose: () => void;
  onSaved: (slug: string, updated: Partial<GiftFund>) => void;
  onImageChange: (cover: string) => void;
}) {
  const { t } = useTranslation();
  const formId = `fund:${fund.slug}`;
  const [initial] = useState<FundTextPayload>(() => fundText(fund));
  // Unsaved edits from an earlier opening are laid over the stored fund.
  const [form, setForm] = useState<FundTextPayload>(() => ({
    ...initial,
    ...((readAutosaveDraft(event.uuid, formId) as Partial<FundTextPayload> | undefined) ?? {}),
  }));
  const [goalInput, setGoalInput] = useState<string>(form.goal ? String(form.goal) : "");
  const [cover, setCover] = useState(fund.cover ?? "");

  const autosave = useAutosave<FundTextPayload, GiftFund>({
    current: form,
    initial,
    resetKey: fund.slug,
    pendingKey: event.uuid,
    formId,
    allowedKeys: FUND_AUTOSAVE_KEYS,
    validate: (p): Record<string, string> => (p.name.trim() ? {} : { name: t("validation.required") }),
    save: (patch) => updateGiftFund(event.uuid, fund.slug, patch),
    onSaved: (updated, patch) => {
      // Never take the image from this response: the image field owns it.
      const { cover: _cover, ...text } = updated ?? {};
      onSaved(fund.slug, { ...patch, ...text });
    },
  });

  const set = (patch: Partial<FundTextPayload>) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {t("gifts.fund.edit")}
        </DialogTitle>
        <DialogDescription>
          {t("events.form.modalGiftDescription", {
          })}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2" onBlur={() => void autosave.flush()}>
        <div className="space-y-2">
          <Label htmlFor="fund-name">{t("events.form.fundName")}</Label>
          <Input
            id="fund-name"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
            placeholder={t("events.form.fundNamePlaceholder")}
          />
          {autosave.fieldErrors.name && (
            <p className="text-sm text-destructive">{autosave.fieldErrors.name}</p>
          )}
        </div>

        <div>
          <GiftImageField
            eventUuid={event.uuid}
            giftUuid={fund.id}
            label={t("events.form.coverImage")}
            value={cover}
            onChange={(url) => {
              setCover(url);
              onImageChange(url);
            }}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("events.form.descriptionLabel")}</Label>
          <Textarea
            rows={3}
            value={form.description}
            onChange={(event) => set({ description: event.target.value })}
            placeholder={t("events.form.descriptionPlaceholder")}
          />
          <div className="space-y-1.5 pt-1">
            <p className="text-xs text-muted-foreground">{t("gifts.fund.inspirationLabel")}</p>
            <div className="flex flex-wrap gap-1.5">
              {(["honeymoon", "newHome", "future"] as const).map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  data-testid={`fund-inspiration-${preset}`}
                  onClick={() => set({ description: t(`gifts.fund.inspirationText.${preset}`) })}
                >
                  {t(`gifts.fund.inspirationTitle.${preset}`)}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("gifts.fund.goalAmount")}</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              value={goalInput}
              onChange={(event) => {
                setGoalInput(event.target.value);
                set({ goal: event.target.value ? Number(event.target.value) : 0 });
              }}
              placeholder="0"
            />
            <span className="text-sm font-medium text-muted-foreground">{fund.currency ?? event.currency ?? "RON"}</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="fund-thank-you">{t("gifts.fund.thankYouMessage")}</Label>
          <Textarea
            id="fund-thank-you"
            rows={3}
            value={form.thankYou}
            onChange={(event) => set({ thankYou: event.target.value })}
            placeholder={t("events.form.thankYouPlaceholder")}
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
          <div>
            <p className="font-medium text-foreground">
              {t("events.form.displayGift")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("events.form.displayGiftHint", {
              })}
            </p>
          </div>
          <Switch
            checked={form.enabled}
            onCheckedChange={(enabled) => {
              autosave.flushOnNextChange();
              set({ enabled });
            }}
          />
        </div>
      </div>

      <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <AutosaveStatus
          className="justify-start"
          status={autosave.status}
          restored={autosave.restored}
          onRetry={() => void autosave.retry()}
          onDiscard={() => {
            autosave.discardDraft();
            setForm(initial);
            setGoalInput(initial.goal ? String(initial.goal) : "");
          }}
          onSaveNow={() => void autosave.flush()}
        />
        <Button variant="outline" onClick={onClose} data-testid="fund-dialog-close">
          {t("common.close")}
        </Button>
      </DialogFooter>
    </>
  );
}
